'use client';

import React, { useEffect, useState } from 'react';

interface ConfettiPiece {
  id: number;
  x: number;
  y: number;
  rotation: number;
  color: string;
  size: number;
  speedX: number;
  speedY: number;
  delay: number;
}

interface ConfettiEffectProps {
  isActive: boolean;
  particleCount?: number;
  duration?: number;
}

const ConfettiEffect: React.FC<ConfettiEffectProps> = ({ 
  isActive, 
  particleCount = 100,
  duration = 5000 
}) => {
  const [pieces, setPieces] = useState<ConfettiPiece[]>([]);
  const colors = [
    '#FF6B6B',
    '#4ECDC4',
    '#45B7D1',
    '#FFA07A',
    '#98D8C8',
    '#6C5CE7',
    '#FDCB6E',
    '#00B894',
    '#E17055',
    '#74B9FF',
    '#A29BFE',
    '#55A3FF',
    '#FFD93D',
    '#6BCF7F',
    '#FF6B9D',
  ];

  useEffect(() => {
    if (isActive) {
      const newPieces: ConfettiPiece[] = [];
      
      for (let i = 0; i < particleCount; i++) {
        newPieces.push({
          id: i,
          x: Math.random() * 100,
          y: -20 - Math.random() * 100, // Start above viewport
          rotation: Math.random() * 360,
          color: colors[Math.floor(Math.random() * colors.length)],
          size: 4 + Math.random() * 6,
          speedX: (Math.random() - 0.5) * 2,
          speedY: 2 + Math.random() * 3,
          delay: Math.random() * 1000, // Random delay for staggered effect
        });
      }
      
      setPieces(newPieces);

      // Clear confetti after duration
      const timer = setTimeout(() => {
        setPieces([]);
      }, duration);

      return () => clearTimeout(timer);
    } else {
      setPieces([]);
    }
  }, [isActive, particleCount, duration]);

  if (!isActive || pieces.length === 0) return null;

  return (
    <div className="fixed inset-0 pointer-events-none z-9999 overflow-hidden">
      {pieces.map((piece) => (
        <div
          key={piece.id}
          className="absolute animate-confetti"
          style={{
            left: `${piece.x}%`,
            top: `${piece.y}%`,
            width: `${piece.size}px`,
            height: `${piece.size * 1.5}px`,
            backgroundColor: piece.color,
            transform: `rotate(${piece.rotation}deg)`,
            animationDelay: `${piece.delay}ms`,
            animationDuration: `${duration}ms`,
            borderRadius: '2px',
            opacity: 0.9,
          }}
        />
      ))}
      
      <style jsx>{`
        @keyframes confetti {
          0% {
            transform: translateY(0) rotate(0deg);
            opacity: 1;
          }
          100% {
            transform: translateY(100vh) rotate(720deg);
            opacity: 0;
          }
        }
        
        .animate-confetti {
          animation: confetti linear forwards;
        }
      `}</style>
    </div>
  );
};

export default ConfettiEffect;